import { memo } from "react";
import { useShallow } from "zustand/react/shallow";

import FiltersMobileTriggerButton from "components/Filters/MobileTriggerButton";
import SearchInput from "components/SearchInput";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Box from "ds/components/Box";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Select from "ds/components/Select";
import SelectInput from "ds/components/Select/Input";
import Typography from "ds/components/Typography";
import { pluralize } from "shared/Pluralize";

import {
  ACCOUNT_IAC_MANAGEMENT_GROUP_BY_OPTIONS,
  STACK_IAC_MANAGEMENT_GROUP_BY_OPTIONS,
} from "../../constants";
import { FILTERS_ORDER_SETTINGS_KEY } from "../../ListLayout/constants";
import { IacManagementGroupBy } from "../../types";
import useIacManagementAnalytics from "../../useIacManagementAnalytics";
import useIacManagementStore from "../../useIacManagementStore";

const IacManagementHeader = () => {
  const { groupBy, setGroupBy, pageIsEmpty, parentStackId, allResourcesQty } =
    useIacManagementStore(
      useShallow((state) => ({
        groupBy: state.groupBy,
        setGroupBy: state.setGroupBy,
        pageIsEmpty: state.pageIsEmpty,
        parentStackId: state.parentStackId,
        allResourcesQty: state.allResourcesQty,
      }))
    );

  const { trackAnalytics } = useIacManagementAnalytics();

  const handleGroupByChange = (groupBy: IacManagementGroupBy) => {
    setGroupBy(groupBy);
    trackAnalytics("Group By Selected", { groupBy });
  };

  return (
    <PageInfoHeader title={parentStackId ? "Resources" : "IaC management"} shouldStackOnMobile>
      <Box shrink="0" margin="0 medium 0 0">
        <Typography variant="p-body3" tag="span" color="secondary">
          {`${allResourcesQty} ${pluralize("resource", allResourcesQty)}`}
        </Typography>
      </Box>

      <ViewHeaderWrapper>
        <SearchInput
          placeholder="Search resources..."
          filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
          disabled={pageIsEmpty}
          callback={() => trackAnalytics("Search Entered")}
        />
        <FiltersMobileTriggerButton />

        <Select
          isDisabled={pageIsEmpty}
          value={groupBy}
          items={
            parentStackId
              ? STACK_IAC_MANAGEMENT_GROUP_BY_OPTIONS
              : ACCOUNT_IAC_MANAGEMENT_GROUP_BY_OPTIONS
          }
          onChange={handleGroupByChange}
          renderInput={(props) => (
            <SelectInput
              {...props}
              renderValue={() => (
                <Typography variant="p-t7" tag="span">
                  Grouped by: {groupBy}
                </Typography>
              )}
            />
          )}
        />
      </ViewHeaderWrapper>
    </PageInfoHeader>
  );
};

export default memo(IacManagementHeader);
